/**
 * IntegrationsTab — Displays current (configured) and available vendor integrations.
 *
 * Props:
 *   yesIntegrations   — array of configured vendor integration records
 *   noIntegrations    — array of available (unconfigured) vendor records
 *   editRights        — boolean (can edit/add/delete)
 *   onAdd             — (vendorItem) => void — open add drawer
 *   onEdit            — (integrationItem) => void — open edit drawer
 *   onActivate        — (integrationItem) => void — activate integration
 *   onDeactivate      — (integrationItem) => void — deactivate integration
 *   onDelete          — (integrationItem) => void — delete integration
 *   onDownloadTemplate — (integrationItem) => void — download onboarding template
 *   onUploadTemplate  — (integrationItem) => void — open upload dialog
 *   onMailboxCredentials — (integrationItem) => void — open Yardi mailbox credentials drawer
 *   hasYardiMailbox   — boolean — whether mailbox credentials already exist (Add vs Edit label)
 *   yardiVendorId     — number — vendor_id that requires the mailbox credentials step
 *   saving            — boolean (disable actions while saving)
 *   downloadingIndex  — index of integration currently downloading template (or -1)
 */

import { useState } from 'react';
import imgMap from '../../../../../components/component-assets/ImageMap.jsx';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Resolve vendor logo — returns img element or text fallback */
const VendorLogo = ({ item }) => {
  const logo = item.logo_file ? imgMap[item.logo_file] : null;
  const name = item.vendor_name || 'Unknown Vendor';

  if (logo && item.website_url) {
    return (
      <a href={item.website_url} target="_blank" rel="noreferrer">
        <img className="cpp-int-card__logo" src={logo} alt={`${name} Logo`} />
      </a>
    );
  }
  if (logo) {
    return <img className="cpp-int-card__logo" src={logo} alt={`${name} Logo`} />;
  }
  if (item.website_url) {
    return (
      <a href={item.website_url} target="_blank" rel="noreferrer" className="cpp-int-card__name-link">
        {name}
      </a>
    );
  }
  return <span className="cpp-int-card__name-text">{name}</span>;
};

/** Determine truthy from 1, true, or boolean */
const isTruthy = (val) => val === 1 || val === true;

// ── Component ────────────────────────────────────────────────────────────────

const IntegrationsTab = ({
  yesIntegrations = [],
  noIntegrations = [],
  editRights,
  onAdd,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
  onDownloadTemplate,
  onUploadTemplate,
  onMailboxCredentials,
  hasYardiMailbox = false,
  yardiVendorId,
  saving,
  downloadingIndex = -1,
}) => {
  // Local state for credential/auth token reveal
  const [revealedCredentials, setRevealedCredentials] = useState(new Set());
  const [revealedAuthTokens, setRevealedAuthTokens] = useState(new Set());

  const toggleReveal = (setter, key) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── Current Integrations ───────────────────────────────────────────────
  return (
    <>
      {/* Current Integrations */}
      <section className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">
            Current Integrations
            <span className="pp-section__count">({yesIntegrations.length})</span>
          </h3>
          <div className="pp-section__actions">
            <span className="cpp-int-badge cpp-int-badge--active">
              {yesIntegrations.length} Active
            </span>
          </div>
        </div>

        {yesIntegrations.length === 0 ? (
          <div className="pp-section__body cpp-int-empty">
            <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="var(--color-text-tertiary, #9ca3af)" className="cpp-int-empty__icon" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
            </svg>
            <p className="cpp-int-empty__title">No Active Integrations</p>
            <p className="cpp-int-empty__subtitle">
              Set up integrations below to connect with third-party services
            </p>
          </div>
        ) : (
          <div className="pp-section__body" style={{ padding: 0 }}>
            {yesIntegrations.map((item, index) => {
              const key = item.map_id || index;
              const credRevealed = revealedCredentials.has(key);
              const authRevealed = revealedAuthTokens.has(key);
              // Yardi (vendor 7) requires mailbox credentials as a second setup
              // step before it can be activated.
              const isYardi = yardiVendorId != null && item.vendor_id === yardiVendorId;
              const needsMailbox = isYardi && !hasYardiMailbox;

              return (
                <div key={key} className="cpp-int-card">
                  {/* Logo / Name */}
                  <div className="cpp-int-card__logo-col">
                    <VendorLogo item={item} />
                  </div>

                  {/* Middle section — credentials + auth + features */}
                  <div className="cpp-int-card__details">
                    {/* Edit button */}
                    {editRights && (
                      <button
                        type="button"
                        className="dp-btn dp-btn--secondary dp-btn--sm"
                        onClick={() => onEdit(item, index)}
                        disabled={saving}
                        style={{ marginBottom: '0.75rem', alignSelf: 'flex-start' }}
                      >
                        Edit
                      </button>
                    )}

                    {/* Credentials */}
                    <div className="cpp-int-card__field-row">
                      <span className="cpp-int-card__field-label">Credentials</span>
                      {item.credentials ? (
                        <span className="cpp-int-card__field-value">
                          <span
                            className="cpp-credential-text"
                            onClick={() => toggleReveal(setRevealedCredentials, key)}
                            role="button"
                            tabIndex={0}
                          >
                            {credRevealed ? item.credentials : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                          </span>
                          <button
                            type="button"
                            className="dp-btn dp-btn--ghost dp-btn--sm cpp-reveal-btn"
                            onClick={() => toggleReveal(setRevealedCredentials, key)}
                            title={credRevealed ? 'Hide credentials' : 'Show credentials'}
                            aria-label={credRevealed ? 'Hide credentials' : 'Show credentials'}
                          >
                            {credRevealed ? (
                              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                              </svg>
                            ) : (
                              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                              </svg>
                            )}
                          </button>
                        </span>
                      ) : (
                        <span className="cpp-int-card__field-value dp-cell-text--muted">N/A</span>
                      )}
                    </div>

                    {/* Auth Token */}
                    <div className="cpp-int-card__field-row">
                      <span className="cpp-int-card__field-label">Auth Token</span>
                      {item.auth ? (
                        <span className="cpp-int-card__field-value">
                          <span
                            className="cpp-credential-text"
                            onClick={() => toggleReveal(setRevealedAuthTokens, key)}
                            role="button"
                            tabIndex={0}
                          >
                            {authRevealed ? item.auth : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                          </span>
                          <button
                            type="button"
                            className="dp-btn dp-btn--ghost dp-btn--sm cpp-reveal-btn"
                            onClick={() => toggleReveal(setRevealedAuthTokens, key)}
                            title={authRevealed ? 'Hide auth token' : 'Show auth token'}
                            aria-label={authRevealed ? 'Hide auth token' : 'Show auth token'}
                          >
                            {authRevealed ? (
                              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                              </svg>
                            ) : (
                              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                              </svg>
                            )}
                          </button>
                        </span>
                      ) : (
                        <span className="cpp-int-card__field-value dp-cell-text--muted">N/A</span>
                      )}
                    </div>

                    {/* Features */}
                    <div className="cpp-int-card__field-row">
                      <span className="cpp-int-card__field-label">Features</span>
                      <span className="cpp-int-card__field-value">
                        {(() => {
                          const labels = [];
                          if (isTruthy(item.is_receiving)) labels.push('Receiving Residents');
                          if (isTruthy(item.is_receiving_user)) labels.push('Receiving Staff');
                          if (isTruthy(item.is_sending)) labels.push('Sending Nurse Call Events');
                          return labels.length === 0
                            ? <span className="dp-cell-text--muted">None</span>
                            : labels.join(' | ');
                        })()}
                      </span>
                    </div>

                    {/* Status */}
                    <div className="cpp-int-card__field-row">
                      <span className="cpp-int-card__field-label">Status</span>
                      <span className={`cpp-int-status ${item.is_active ? 'cpp-int-status--active' : 'cpp-int-status--inactive'}`}>
                        {item.is_active ? 'Live' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Actions column */}
                  {editRights && (
                    <div className="cpp-int-card__actions">
                      {item.is_active ? (
                        <button
                          type="button"
                          className="dp-btn dp-btn--danger dp-btn--sm"
                          onClick={() => onDeactivate(item, index)}
                          disabled={saving}
                          style={{ minWidth: '100px' }}
                        >
                          De-activate
                        </button>
                      ) : (
                        <>
                          {/* Yardi (vendor 7) — mailbox credentials must be set up before activation */}
                          {isYardi && (
                            <button
                              type="button"
                              className="dp-btn dp-btn--secondary dp-btn--sm"
                              onClick={() => onMailboxCredentials(item, index)}
                              disabled={saving}
                              style={{ minWidth: '100px' }}
                            >
                              {hasYardiMailbox ? 'Edit Mailbox Credentials' : 'Add Mailbox Credentials'}
                            </button>
                          )}
                          {/* Template dropdown */}
                          <div className="cpp-int-template-group">
                            {downloadingIndex === index ? (
                              <button type="button" className="dp-btn dp-btn--primary dp-btn--sm" disabled>
                                Loading...
                              </button>
                            ) : (
                              <div className="cpp-int-template-btns">
                                <button
                                  type="button"
                                  className="dp-btn dp-btn--secondary dp-btn--sm"
                                  onClick={() => onDownloadTemplate(item, index)}
                                  disabled={saving}
                                  title="Download onboarding template"
                                >
                                  <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                  </svg>
                                  Download
                                </button>
                                <button
                                  type="button"
                                  className="dp-btn dp-btn--secondary dp-btn--sm"
                                  onClick={() => onUploadTemplate(item, index)}
                                  disabled={saving}
                                  title="Upload onboarding template"
                                >
                                  <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                                  </svg>
                                  Upload
                                </button>
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            className="dp-btn dp-btn--primary dp-btn--sm"
                            onClick={() => onActivate(item, index)}
                            disabled={saving || needsMailbox}
                            style={{ minWidth: '100px' }}
                            title={needsMailbox ? 'Add mailbox credentials before activating' : undefined}
                          >
                            Activate
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Available Integrations */}
      <section className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">
            Available Integrations
            <span className="pp-section__count">({noIntegrations.length})</span>
          </h3>
          <div className="pp-section__actions">
            <span className="cpp-int-badge cpp-int-badge--available">
              {noIntegrations.length} Available
            </span>
          </div>
        </div>

        {noIntegrations.length === 0 ? (
          <div className="pp-section__body cpp-int-empty">
            <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="var(--color-text-tertiary, #9ca3af)" className="cpp-int-empty__icon" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <p className="cpp-int-empty__title">No Available Integrations</p>
            <p className="cpp-int-empty__subtitle">
              Check back later for new integration options
            </p>
          </div>
        ) : (
          <div className="pp-section__body" style={{ padding: 0 }}>
            {noIntegrations.map((item, index) => {
              const key = item.vendor_id || index;

              return (
                <div key={key} className="cpp-int-card">
                  {/* Logo / Name */}
                  <div className="cpp-int-card__logo-col">
                    <VendorLogo item={item} />
                  </div>

                  {/* Details */}
                  <div className="cpp-int-card__details">
                    <div className="cpp-int-card__field-row">
                      <span className="cpp-int-card__field-label">API Method</span>
                      <span className="cpp-int-card__field-value">
                        {item.auth_type_name || 'Unknown'}
                      </span>
                    </div>
                    <div className="cpp-int-card__field-row">
                      <span className="cpp-int-card__field-label">Features</span>
                      <span className="cpp-int-card__field-value">
                        {(() => {
                          const labels = [];
                          if (isTruthy(item.feature_receive)) labels.push('Receive Residents');
                          if (isTruthy(item.feature_user_receive)) labels.push('Receive Staff');
                          if (isTruthy(item.feature_send)) labels.push('Send Nurse Call Events');
                          return labels.length === 0
                            ? <span className="dp-cell-text--muted">None</span>
                            : labels.join(' | ');
                        })()}
                      </span>
                    </div>
                  </div>

                  {/* Actions column */}
                  {editRights && (
                    <div className="cpp-int-card__actions">
                      {isTruthy(item.is_active) ? (
                        <button
                          type="button"
                          className="dp-btn dp-btn--primary dp-btn--sm"
                          onClick={() => onAdd(item, index)}
                          disabled={saving}
                          style={{ minWidth: '100px' }}
                        >
                          Set-up
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="dp-btn dp-btn--secondary dp-btn--sm"
                          disabled
                          style={{ minWidth: '100px' }}
                        >
                          Unavailable
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
};

export default IntegrationsTab;
